import { graphql } from "@apollo/client/react/hoc"
import Button from "@pathwright/ui/src/components/button/Button"
import withTranslate from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import View from "@pathwright/ui/src/components/ui/View"
import { COHORTS_INCLUDED_PRODUCTS_QUERY } from "@pathwright/web/src/modules/cohort/graphql"
import { BELLTOWER_SCHOOL_ID } from "@pathwright/web/src/modules/store/constants"
import { flattenEdges } from "@pathwright/web/src/modules/utils/apollo"
import ConnectStores from "lib/core/store/components/ConnectStores"
import get from "lodash/get"
import styled from "styled-components"
import ResourceOffering from "./ResourceOffering"

const StyledLoader = styled.div`
  position: relative;
  min-height: 50px;
`

const ResourceOfferings = ({
  resource,
  offeringsList,
  isLoading,
  can_add_group,
  loadingMore,
  hasMore,
  loadMore,
  t,
  tc
}) => (
  <View className="ResourceOffering__List" paddingLeft paddingRight>
    {isLoading && (!offeringsList || !offeringsList.length) ? (
      <LoadingCircle />
    ) : (
      offeringsList.map((offering) => (
        <ResourceOffering
          key={offering.id}
          resource={resource}
          offering={offering}
        />
      ))
    )}
    {loadingMore ? (
      <StyledLoader>
        <LoadingCircle />
      </StyledLoader>
    ) : hasMore ? (
      <Button styleType="empty" stretch onClick={loadMore}>
        {tc("load more")}
      </Button>
    ) : null}
  </View>
)

ResourceOfferings.displayName = "ResourceOfferings"

const mapStateToProps = function (state, ownProps) {
  let {
    resource,
    offerings,
    offerings_loading: isLoading,
    offerings_pagination,
    offerings_loading_more: loadingMore
  } = state.resourceStore.state

  const { school } = state.schoolStore.state
  const { subscription } = state.subscribeStore.state
  const permissions = state.resourceStore.request.permissions()

  const resourceLicensedFromBelltower =
    resource && resource.licensed_from_school === BELLTOWER_SCHOOL_ID

  const can_add_group =
    school.is_curriculum_enabled &&
    !!permissions &&
    permissions.can_edit_resource &&
    !resourceLicensedFromBelltower

  // compbined offerings and license offerings
  const offeringsList = []

  if (offerings) {
    offeringsList.push(
      ...offerings.filter((o) => !o.parent_offering_id && !o.is_curriculum)
    )

    if (
      resource &&
      resource.licensing_enabled &&
      resource.license_offerings &&
      resource.license_offerings
    ) {
      const curriculumOffering = offerings.find((o) => !o.is_curriculum)

      const licenseOfferings = resource.license_offerings
        /**
         * Filter RLOs based on user's access.
         *
         * Goal is to only include the RLO if:
         *   1) only show private groups RLO if user is subscribed to an included school plan
         *   1) otherwise if group subscription type or
         *   2) there are no group subscription types
         */
        .filter((rlo, i, rlos) => {
          // filter out school licensing RLOs
          if (rlo.licensing_mode === 2) return false

          // if there are no license offeirngs for private cohorts, simply include this RLO
          if (!rlos.find((rlo) => rlo.licensing_mode === 1)) return true

          // if user is subscribed to included school plan, then only include privat cohrot RLOs
          if (
            rlos.find((rlo) =>
              rlo.include_in_subscription_plans.includes(
                get(subscription, "school_plan.id")
              )
            )
          ) {
            return rlo.licensing_mode === 1
          }

          // if there is a group subscription RLO, only include this RLO
          // if it also is a group subscription RLO, thereby excluding the private cohorts type
          if (rlos.find((rlo) => rlo.licensing_mode === 3))
            return rlo.licensing_mode === 3
          // otherwise
          return true
        })
        // massage RLO data into expected <ResourceOffering /> `offering` prop
        .map((rlo) => ({
          id: rlo.id,
          resource_id: rlo.resource,
          is_license_offering: true,
          name: ownProps.t("cohort.resource_offering"),
          description: rlo.display_description,
          price: rlo.cost_per_seat >= 0 ? rlo.cost_per_seat : null,
          currency_display:
            rlo.cost_per_seat >= 0 ? rlo.currency_display_cost_per_seat : "",
          include_with_products: rlo.include_with_products,
          include_with_products_data: rlo.include_with_products_data,
          include_in_subscription_plans: rlo.include_in_subscription_plans,
          // curriculum offering permissions should be same as RLO permissions
          permissions: curriculumOffering ? curriculumOffering.permissions : {},
          licensing_mode: rlo.licensing_mode
        }))

      offeringsList.push(...licenseOfferings)
    }
  }

  // NOTE: due to the hackery above where we filter out some offerings,
  // we'll hide the "Load more" button when we have no filtered offerings
  // to render. Ideally we'd do the above filtering on the server, but we're
  // in PW2 sunset mode now, so this will have to do.
  const hasMore =
    get(offerings_pagination, "has_next", false) && !!offeringsList.length
  const loadMore = state.resourceStore.action.loadMoreOfferings

  const props = _.assign(
    {},
    {
      resource,
      offeringsList,
      isLoading,
      can_add_group,
      loadingMore,
      hasMore,
      loadMore
    },
    ownProps
  )
  return props
}

const stores = [
  {
    store: "resource",
    watch: [
      "offerings",
      "offerings_loading",
      "offering",
      "offerings_loading_more"
    ]
  },
  { store: "school", watch: false },
  { store: "subscribe", watch: false }
]

export const WithResourceOfferingsWithIncludedProducts = graphql(
  COHORTS_INCLUDED_PRODUCTS_QUERY,
  {
    options: (props) => ({
      skip: !props.resource,
      variables: {
        resourceId: get(props, "resource.id"),
        visibilityFilter: { gt: "private" }
      }
    }),
    props: ({ data, ownProps }) => {
      const offerings = get(ownProps, "offeringsList", [])
      const cohorts = flattenEdges(get(data, "groups", []))
      return {
        // map over offerings from resource store and
        // merge include_with_products field from graphql query
        offeringsList: offerings.map((offering) => {
          const cohort = cohorts.find((cohort) => cohort.id === offering.id)
          return {
            ...offering,
            // store in separate key from include_with_products to avoid corrupting expected data shape!
            include_with_products_data: get(cohort, "include_with_products", [])
          }
        })
      }
    }
  }
)

const ResourceOfferingsWithIncludedProducts =
  WithResourceOfferingsWithIncludedProducts(ResourceOfferings)

export default withTranslate(
  ConnectStores(
    ResourceOfferingsWithIncludedProducts,
    stores,
    {},
    mapStateToProps
  )
)
